/**
 * CR-7168: Column manager dropdown for the Submitted Appeals table.
 *
 * Follows the QualityColumnsDropdown pattern:
 *   - "Columns" toolbar button toggles the dropdown
 *   - Each row: drag handle + checkbox + label + visibility eye
 *   - Drag-to-reorder uses native HTML5 dataTransfer
 *   - Outside-click closes the dropdown
 *   - "Select All" / "Reset" actions in the header
 *
 * View and Action columns are excluded - they're always visible and pinned.
 */
import { useEffect, useRef, useState } from 'react';
import { Columns, Eye, EyeOff, GripVertical } from 'lucide-react';
import {
  type AppealColumnConfig,
  DEFAULT_APPEAL_COLUMNS,
  saveAppealColumns,
} from './appealsColumnConfig';

interface AppealsColumnsDropdownProps {
  columns: AppealColumnConfig[];
  onChange: (next: AppealColumnConfig[]) => void;
  userId?: number | string;
}

export function AppealsColumnsDropdown({ columns, onChange, userId }: AppealsColumnsDropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const update = (next: AppealColumnConfig[]) => {
    saveAppealColumns(next, userId);
    onChange(next);
  };

  const toggleColumn = (id: string) =>
    update(columns.map((c) => (c.id === id ? { ...c, visible: !c.visible } : c)));

  const selectAll = () => update(columns.map((c) => ({ ...c, visible: true })));

  const reset = () => update(DEFAULT_APPEAL_COLUMNS);

  const reorder = (from: number, to: number) => {
    const next = [...columns];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    update(next.map((c, i) => ({ ...c, order: i })));
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className={`px-3 py-1.5 rounded-lg border text-sm flex items-center gap-2 ${
          open ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
        }`}
      >
        <Columns className="h-3 w-3" />Columns
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-72 bg-white border border-border rounded-lg shadow-lg z-50 max-h-[400px] overflow-y-auto">
          <div className="p-2 border-b flex items-center justify-between">
            <span className="text-sm font-medium">Manage Columns</span>
            <div className="flex items-center gap-2">
              <button onClick={selectAll} className="text-xs text-primary hover:underline">
                Select All
              </button>
              <span className="text-xs text-muted-foreground">|</span>
              <button onClick={reset} className="text-xs text-primary hover:underline">
                Reset
              </button>
            </div>
          </div>
          <div className="px-2 py-1 text-xs text-muted-foreground border-b bg-muted/30">
            Drag to reorder &middot; Click to show/hide
          </div>
          <div className="p-2 space-y-1">
            {columns.map((col, idx) => (
              <div
                key={col.id}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData('text/plain', String(idx));
                  e.currentTarget.classList.add('opacity-50');
                }}
                onDragEnd={(e) => e.currentTarget.classList.remove('opacity-50')}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.add('bg-primary/10');
                }}
                onDragLeave={(e) => e.currentTarget.classList.remove('bg-primary/10')}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove('bg-primary/10');
                  const from = parseInt(e.dataTransfer.getData('text/plain'), 10);
                  if (from !== idx) reorder(from, idx);
                }}
                className="flex items-center gap-2 px-2 py-1.5 hover:bg-muted/50 rounded cursor-move group"
              >
                <GripVertical className="h-4 w-4 text-muted-foreground/50 group-hover:text-muted-foreground flex-shrink-0" />
                <input
                  type="checkbox"
                  checked={col.visible}
                  onChange={() => toggleColumn(col.id)}
                  onClick={(e) => e.stopPropagation()}
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
                />
                <span className="text-sm flex-1">{col.label}</span>
                {col.visible ? (
                  <Eye className="h-3 w-3 text-muted-foreground" />
                ) : (
                  <EyeOff className="h-3 w-3 text-muted-foreground" />
                )}
              </div>
            ))}
          </div>
          <div className="px-2 py-1.5 text-xs text-muted-foreground border-t bg-muted/30">
            View and Action columns are always visible
          </div>
        </div>
      )}
    </div>
  );
}
